import { useState, useEffect, useCallback } from 'react';
import { useMeeting } from '@videosdk.live/react-sdk';

const useVideoQuality = () => {
  const [participantCount, setParticipantCount] = useState(0);
  const [networkQuality, setNetworkQuality] = useState('medium');
  const [contentType, setContentType] = useState('motion'); // 'motion', 'text', 'detail'
  const [autoQualityEnabled, setAutoQualityEnabled] = useState(true);

  const meeting = useMeeting();
  const participants = meeting?.participants;

  // Monitor participant count changes
  useEffect(() => {
    if (participants) {
      const count = new Map(participants).size + 1; // +1 for local participant
      setParticipantCount(count);
    }
  }, [participants]);

  // Network quality monitoring
  useEffect(() => {
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    
    const updateNetworkQuality = () => {
      if (!connection) {
        setNetworkQuality('unknown');
        return;
      }

      const { effectiveType, downlink, rtt } = connection;
      
      if (effectiveType === '4g' && downlink >= 10 && rtt < 100) {
        setNetworkQuality('high');
      } else if (effectiveType === '4g' || (downlink >= 5 && rtt < 200)) {
        setNetworkQuality('medium');
      } else {
        setNetworkQuality('low');
      }
    };

    updateNetworkQuality();
    
    if (connection) {
      connection.addEventListener('change', updateNetworkQuality);
      return () => connection.removeEventListener('change', updateNetworkQuality);
    }
  }, []);

  // Auto quality adjustment based on conditions
  const getRecommendedQuality = useCallback(() => {
    if (!autoQualityEnabled) return null;

    const isMobile = window.matchMedia("(max-width: 768px)").matches;
    
    // High quality conditions
    if (participantCount <= 4 && networkQuality === 'high' && !isMobile) {
      return 'high'; // h720p_w1280p or better
    }
    
    // Medium quality conditions
    if (participantCount <= 8 && networkQuality !== 'low') {
      return 'medium'; // h480p_w640p
    }
    
    // Low quality conditions
    return 'low'; // h360p_w640p or h240p_w320p
  }, [participantCount, networkQuality, autoQualityEnabled]);

  // Quality switching method
  const switchVideoQuality = useCallback(async (quality) => {
    if (!meeting) return;

    try {
      // This would integrate with your video track creation
      const { createCameraVideoTrack } = await import('@videosdk.live/react-sdk');
      
      let encoderConfig;
      let multiStream = true;
      
      switch (quality) {
        case 'high':
          encoderConfig = 'h720p_w1280p';
          break;
        case 'medium':
          encoderConfig = 'h480p_w640p';
          break;
        case 'low':
          encoderConfig = 'h360p_w640p';
          multiStream = false;
          break;
        default:
          encoderConfig = 'h480p_w640p';
      }

      if (meeting.localWebcamOn) {
        meeting.disableWebcam();
        
        // Create new track with updated quality
        const customTrack = await createCameraVideoTrack({
          encoderConfig,
          optimizationMode: contentType,
          multiStream,
          facingMode: window.matchMedia("(max-width: 768px)").matches ? "user" : "environment",
        });
        
        meeting.enableWebcam(customTrack);
      }
    } catch (error) {
      console.error('Error switching video quality:', error);
    }
  }, [meeting, contentType]);

  // Content type optimization
  const optimizeForContentType = useCallback((type) => {
    setContentType(type);
    
    // Auto-switch quality when content type changes
    if (autoQualityEnabled && meeting?.localWebcamOn) {
      const recommendedQuality = getRecommendedQuality();
      if (recommendedQuality) {
        switchVideoQuality(recommendedQuality);
      }
    }
  }, [autoQualityEnabled, meeting, getRecommendedQuality, switchVideoQuality]);

  return {
    participantCount,
    networkQuality,
    contentType,
    autoQualityEnabled,
    recommendedQuality: getRecommendedQuality(),
    
    // Controls
    setAutoQualityEnabled,
    switchVideoQuality,
    optimizeForContentType,
    
    // Utilities
    isHighQualityRecommended: getRecommendedQuality() === 'high',
    isLowQualityRecommended: getRecommendedQuality() === 'low',
  };
};

export default useVideoQuality;